const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_HEADER_NAME = 'X-Requested-With';
const CSRF_HEADER_VALUE = 'XMLHttpRequest';

const isSameOrigin = (input: RequestInfo | URL): boolean => {
  const url = input instanceof Request ? input.url : String(input);
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

const requestMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
  if (init?.method) return init.method.toUpperCase();
  if (input instanceof Request) return input.method.toUpperCase();
  return 'GET';
};

/**
 * Wrap window.fetch so every same-origin unsafe request carries the CSRF
 * header. Installed once at app boot (main.tsx); raw fetch call sites across
 * the app need no changes.
 *
 * The header is a constant, not a secret — see app/middleware/csrf.py. Its
 * presence is the entire control: a cross-origin attacker restricted to
 * simple requests cannot set a custom header at all, and anything that can
 * set one has already been through a CORS preflight. Because there is no
 * per-session value, there is nothing to fetch, hydrate, or invalidate, so a
 * request can never race ahead of its own credentials.
 */
export const installCsrfFetchInterceptor = (): void => {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!UNSAFE_METHODS.has(requestMethod(input, init)) || !isSameOrigin(input)) {
      return originalFetch(input, init);
    }
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has(CSRF_HEADER_NAME)) {
      headers.set(CSRF_HEADER_NAME, CSRF_HEADER_VALUE);
    }
    return originalFetch(input, { ...init, headers });
  };
};
